import {TextureType as PlatformTextureType} from "@cm/material-nodes"
import {z} from "zod"
import type {Format, SourceInfo, SourceInfoRequest, InputMapsInfo, MaterialDetails} from "#material-maps-exporter/core/types"
import {estimateCommonSize} from "#material-maps-exporter/targets/pbr/image-processing-graph"
import {getImageMapInfos} from "#material-maps-exporter/core/image-processing-graph"
import type {
    Config,
    ConversionConfig,
    ConversionInfoRequest,
    ConversionRequest,
    Engine,
    Folder,
    Request,
    TextureType,
} from "#material-maps-exporter/targets/pbr/types"
import {MAPS_EXPORT_CONFIG_SCHEMA_STR, SRGB_TEXTURE_TYPES_FOR_ENGINE, TEXTURE_TYPES_FOR_ENGINE} from "#material-maps-exporter/targets/pbr/types"
import {normalYToString, workflowToString} from "#material-maps-exporter/targets/pbr/utils"

function anisotropyDescriptionForEngine(engine: Engine):
    | {
          "Anisotropy Strength": string
          "Anisotropy Rotation": string
      }
    | {
          Anisotropy: string
      } {
    switch (engine) {
        case "cm":
            return {Anisotropy: "As one map"}
        case "corona":
            return {"Anisotropy Strength": "0.5 ... 1.0", "Anisotropy Rotation": "Clockwise"}
        case "vray":
            return {"Anisotropy Strength": "0 ... 1.0", "Anisotropy Rotation": "Clockwise"}
        case "cycles":
            return {"Anisotropy Strength": "0 ... 1.0", "Anisotropy Rotation": "Counter-clockwise"}
        default:
            throw Error(`Unsupported engine: ${engine}`)
    }
}

function generateConversionInfoForConversionInfoRequest(conversionRequest: ConversionRequest, materialDetails: MaterialDetails, mapsInfo: InputMapsInfo) {
    const imageMapsInfo = getImageMapInfos(mapsInfo)

    const productName = materialDetails.tagAssignments[0]?.tag.name ?? ""
    const physicalHeight = imageMapsInfo.length > 0 ? imageMapsInfo[0].physicalHeight.toFixed(2) : "?"
    const physicalWidth = imageMapsInfo.length > 0 ? imageMapsInfo[0].physicalWidth.toFixed(2) : "?"
    const displacementScale = imageMapsInfo.find((info) => info.textureType === PlatformTextureType.Displacement)?.displacementScale?.toFixed(2)

    const {resX: width, resY: height, dpi: estimatedDpi} = estimateCommonSize(imageMapsInfo, conversionRequest.resolution)
    const dpi = estimatedDpi?.toFixed(0) ?? "?"
    const resolution = conversionRequest.resolution === "dpi72" ? "low" : "original"

    const entries: [string, string][] = []
    entries.push(["Product name", productName])
    entries.push(["Article ID", `${materialDetails.articleId ?? "None"}`])
    entries.push(["Exported for workflow", `${workflowToString(conversionRequest.workflow)}`])
    entries.push(["Resolution", `${dpi} DPI (${resolution})`])
    entries.push(["Width", `${physicalWidth} cm (${width} px)`])
    entries.push(["Height", `${physicalHeight} cm (${height} px)`])
    entries.push(["Normal map orientation", `${normalYToString(conversionRequest.normalY)}`])
    if (displacementScale !== undefined) entries.push(["Displacement scale", `${displacementScale} cm`])
    Object.entries(anisotropyDescriptionForEngine(conversionRequest.engine)).map(([key, value]) => entries.push([key, value]))
    entries.push([
        "Transmission",
        "The amount and color of light transmitted through material surface. Black indicates opaque and white either fully transparent or highly transmissive areas",
    ])

    return entries
}

export async function exportConfigForExportRequest(
    request: Request,
    materialDetails: MaterialDetails,
    sourceInfo: SourceInfo,
    mapsInfo: InputMapsInfo,
    generateConvertedMapFilename: (materialDetails: MaterialDetails, conversionRequest: ConversionRequest, mapType: TextureType) => Promise<string | null>,
    generateConversionInfoFilename: (materialDetails: MaterialDetails, conversionInfoRequest: ConversionInfoRequest) => Promise<string | null>,
): Promise<Config> {
    const root: Folder = {
        type: "exportFolder",
        content: [],
        name: request.root.name,
    }

    const traverse = async (requestFolder: Request["root"], configFolder: Config["root"]) => {
        for (const item of requestFolder.content) {
            if (item.type === "exportFolder") {
                const newFolder: Config["root"] = {
                    type: "exportFolder",
                    name: item.name,
                    content: [],
                }
                configFolder.content.push(newFolder)
                await traverse(item, newFolder)
            } else if (item.type === "conversionRequest") {
                const format = item.format
                const mapsFilenames = TEXTURE_TYPES_FOR_ENGINE[item.workflow][item.engine]
                configFolder.content.push({
                    ...item,
                    type: "conversionConfig",
                    maps: await Promise.all(
                        mapsFilenames.map(async (fn) => {
                            const filename =
                                (await generateConvertedMapFilename(materialDetails, item, fn)) ??
                                generateDefaultConvertedMapFilename(materialDetails, fn, format)
                            return {type: "convertedMap", name: fn, filename}
                        }),
                    ),
                })
            } else if (item.type === "conversionInfoRequest") {
                const infoContent = generateConversionInfoForConversionInfoRequest(item.conversionRequest, materialDetails, mapsInfo)
                if (item.format === "json")
                    configFolder.content.push({
                        type: "exportJsonFile",
                        name: (await generateConversionInfoFilename(materialDetails, item)) ?? `${item.name}.json`,
                        content: Object.fromEntries(infoContent),
                    })
                else if (item.format === "text") {
                    configFolder.content.push({
                        type: "exportTextFile",
                        name: (await generateConversionInfoFilename(materialDetails, item)) ?? `${item.name}.txt`,
                        content: infoContent.map((entry) => `${entry[0]}: ${entry[1]}`).join("\n"),
                    })
                } else throw Error(`Unknown format in conversion info request: ${item.format}`)
            } else {
                throw Error(`Unknown type in export request to config transform: ${item["type"]}`)
            }
        }
    }

    await traverse(request.root, root)
    return {
        type: "exportConfig",
        schema: MAPS_EXPORT_CONFIG_SCHEMA_STR,
        materialId: materialDetails.legacyId,
        sourceInfo: sourceInfo,
        state: "processing",
        root: root,
    }
}

function generateDefaultConvertedMapFilename(materialDetails: MaterialDetails, mapName: string, ext: Format) {
    return materialDetails.articleId != "" && materialDetails.articleId != null
        ? `${materialDetails.articleId}-${mapName}.${ext}`
        : `${materialDetails.legacyId}-${materialDetails.name}-${mapName}.${ext}`
}

export function useLinearSpace(config: ConversionConfig, mapName: string) {
    return config.format === "exr" || !SRGB_TEXTURE_TYPES_FOR_ENGINE[config.workflow][config.engine].map((x): string => x).includes(mapName)
}

// Factory functions

export function conversionRequest(
    workflow: ConversionRequest["workflow"],
    engine: ConversionRequest["engine"],
    normalY: ConversionRequest["normalY"],
    format: ConversionRequest["format"],
    resolution: ConversionRequest["resolution"],
): ConversionRequest {
    return {type: "conversionRequest", workflow, engine, normalY, format, resolution}
}

export function exportRequest(root: Request["root"], sourceInfoRequest: SourceInfoRequest): Request {
    return {type: "exportRequest", root, sourceInfoRequest}
}

export function exportFolder(content: Request["root"]["content"], name: Folder["name"]): Request["root"] {
    return {type: "exportFolder", content, name}
}

export function conversionInfoRequest(
    conversionRequest: ConversionInfoRequest["conversionRequest"],
    name: ConversionInfoRequest["name"],
    format: ConversionInfoRequest["format"],
): ConversionInfoRequest {
    return {type: "conversionInfoRequest", conversionRequest: conversionRequest, name: name, format: format}
}

export function collectConversionConfigsForExportConfig(exportConfig: Config | Request) {
    const conversionConfigs: (ConversionConfig | ConversionRequest)[] = []

    const traverse = (folder: (typeof exportConfig)["root"]) => {
        folder.content.map((item) => {
            switch (item.type) {
                case "exportFolder":
                    traverse(item)
                    break
                case "conversionConfig":
                case "conversionRequest":
                    conversionConfigs.push(item)
                    break
            }
        })
    }
    traverse(exportConfig.root)
    return conversionConfigs
}

export function isExportConfig(value: unknown): value is Config {
    return z
        .object({
            type: z.literal("exportConfig"),
        })
        .safeParse(value).success
}
